<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

// Inbox arquivada (conexão removida) guarda o histórico, mas não é uma caixa
// configurada: contá-la aqui fazia a tela divergir das Integrações. Mesma
// régua da tela de equipes.
const inboxesList = computed(() => {
  return inboxes.value
    ?.filter(inbox => !/^\[ARQUIVADA\]/i.test(inbox.name || ''))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const inboxSummary = computed(() => {
  const list = inboxesList.value || [];
  const whatsapp = list.filter(inbox =>
    ['Channel::Api', 'Channel::Whatsapp'].includes(inbox.channel_type)
  );
  const social = list.filter(inbox =>
    ['Channel::Instagram', 'Channel::FacebookPage'].includes(inbox.channel_type)
  );
  const attention = whatsapp.filter(inbox => {
    const state = inbox.additional_attributes?.connection_status;
    return ['connecting', 'failed', 'disconnected'].includes(state);
  });

  return {
    total: list.length,
    whatsapp: whatsapp.length,
    social: social.length,
    attention: attention.length,
  };
});

const inboxStatus = inbox => {
  const state = inbox.additional_attributes?.connection_status;
  if (state === 'connected') return { label: 'Conectada', tone: 'green' };
  if (state === 'connecting') return { label: 'Conectando', tone: 'amber' };
  if (state === 'failed') return { label: 'Atenção', tone: 'ruby' };
  if (inbox.channel_type === 'Channel::Api') return { label: 'Pronta', tone: 'blue' };
  return { label: 'Configurada', tone: 'slate' };
};

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <section
        v-else
        class="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        aria-label="Resumo das integrações"
      >
        <div class="rounded-2xl border border-n-weak bg-n-alpha-2 p-4 shadow-sm">
          <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">Total</span>
          <strong class="mt-2 block text-2xl text-n-slate-12">{{ inboxSummary.total }}</strong>
          <span class="mt-1 block text-xs text-n-slate-10">
            caixas de entrada configuradas
          </span>
        </div>
        <div class="rounded-2xl border border-n-green-5 bg-n-green-2 p-4 shadow-sm">
          <span class="text-xs font-medium uppercase tracking-wide text-n-green-11">WhatsApp</span>
          <strong class="mt-2 block text-2xl text-n-green-12">{{ inboxSummary.whatsapp }}</strong>
          <span class="mt-1 block text-xs text-n-green-11">instâncias e API oficial</span>
        </div>
        <div class="rounded-2xl border border-n-blue-5 bg-n-blue-2 p-4 shadow-sm">
          <span class="text-xs font-medium uppercase tracking-wide text-n-blue-11">Sociais</span>
          <strong class="mt-2 block text-2xl text-n-blue-12">{{ inboxSummary.social }}</strong>
          <span class="mt-1 block text-xs text-n-blue-11">Instagram e Facebook</span>
        </div>
        <div class="rounded-2xl border border-n-amber-5 bg-n-amber-2 p-4 shadow-sm">
          <span class="text-xs font-medium uppercase tracking-wide text-n-amber-11">Atenção</span>
          <strong class="mt-2 block text-2xl text-n-amber-12">{{ inboxSummary.attention }}</strong>
          <span class="mt-1 block text-xs text-n-amber-11">conexões para revisar</span>
        </div>
      </section>
      <div v-if="filteredInboxesList.length" class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="group flex min-h-32 flex-col justify-between gap-5 rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-n-strong hover:shadow-md"
        >
          <div class="flex items-start gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="min-w-0 flex-1 flex flex-col items-start gap-1">
              <div class="flex w-full items-start justify-between gap-3">
                <span class="truncate text-heading-3 text-n-slate-12 capitalize">
                {{ inbox.name }}
                </span>
                <span
                  class="shrink-0 rounded-full px-2 py-1 text-xs font-medium"
                  :class="{
                    'bg-n-green-2 text-n-green-11': inboxStatus(inbox).tone === 'green',
                    'bg-n-amber-2 text-n-amber-11': inboxStatus(inbox).tone === 'amber',
                    'bg-n-ruby-2 text-n-ruby-11': inboxStatus(inbox).tone === 'ruby',
                    'bg-n-blue-2 text-n-blue-11': inboxStatus(inbox).tone === 'blue',
                    'bg-n-slate-3 text-n-slate-11': inboxStatus(inbox).tone === 'slate',
                  }"
                >{{ inboxStatus(inbox).label }}</span>
              </div>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                class="text-body-main text-n-slate-11"
              />
            </div>
          </div>
          <div class="flex items-center justify-between border-t border-n-weak pt-3">
            <span class="text-xs text-n-slate-10">Caixa de entrada #{{ inbox.id }}</span>
            <div class="flex gap-2">
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
            </div>
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
